import { useState, useEffect } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { Modal } from "antd";
import { WarningOutlined } from "@ant-design/icons";
import db from "../database";
import { isPastBusinessDay } from "../utils/tillBusinessDate";
import usePOSConfigStore from "../stores/usePOSConfigStore";
import useSessionStore from "../stores/useSessionStore";
import useAuthStore from "../stores/useAuthStore";

const TillModalHandler = () => {
  const [showTillModal, setShowTillModal] = useState(false);
  const history = useHistory();
  const location = useLocation();

  useEffect(() => {
    const checkTillEvents = async () => {
      try {
        // Only check at entry-point routes — never interrupt active POS sessions.
        // A till opened before midnight continues on its opening business date.
        const currentPath = location.pathname;
        const activeSessionPaths = [
          "/preorder",
          "/restaurant",
          "/production",
          "/estimate",
          "/close-till",
          "/close-till-all",
          "/cash-to-keep",
          "/sign-in",
          "/open-till",
          "/hardware",
          "/plugins",
          "/updates",
          "/customer-display",
          "/signage",
          "/template-designer",
          // Till selection — Till.js has its own day-change detection and
          // narrows tillAccess before pushing to /close-till. Showing this
          // modal on /till redirected to /close-till-all prematurely, whose
          // guard bounced back to /till because tillAccess was still an
          // array, which fired the modal again → infinite loop.
          "/till",
          // Non-POS modules — till closure check not relevant here
          "/purchaseOrder",
          "/inventory",
          "/aggregator",
          "/call-center",
          "/dashboard2",
          "/sync",
          "/dualscreen",
        ];
        const isActiveSession = activeSessionPaths.some((path) => currentPath.startsWith(path));
        if (currentPath === "/" || isActiveSession) return;

        const tillEvents = await db?.tillEvents.where("tillStatus").equals("open").toArray();
        if (tillEvents?.length > 0) {
          // BUG-2171: business date, not opening date — a till opened last evening for
          // today (NXT post-close sales) is not a pending session from a previous day.
          if (isPastBusinessDay(tillEvents[0])) {
            // If tillSession is gone (already closed), clean up the stale tillEvent
            const tillSession = useSessionStore.getState().tillSession;
            if (!tillSession) {
              for (const event of tillEvents) {
                await db.tillEvents.where("tillSessionId").equals(event.tillSessionId).modify({ tillStatus: "closed" });
              }
              return;
            }

            // Waiters (matched by salesRep) don't own a till — the closure prompt
            // and /close-till are for cashiers/managers only. Mirrors the same
            // matchedRep check used at Till.js:461 and Login/index.js:278.
            const username = localStorage.getItem("username") || "";
            const salesReps = useAuthStore.getState().tillData?.tillAccess?.csBunit?.salesRep || [];
            const matchedRep = salesReps.find((rep) => rep?.name === username);
            if (matchedRep) return;

            setShowTillModal(true);
          }
        }
      } catch (err) {
        console.error("Till event check failed:", err);
      }
    };

    const runCheck = () => {
      const posConfig = usePOSConfigStore.getState().posConfig;
      if (posConfig?.enableAutoStoreClose === "Y") {
        checkTillEvents();
      }
    };

    runCheck();

    // The pathname-change trigger above only fires on in-app navigation.
    // If the till is left open and the tab/window is simply brought back to
    // the foreground overnight (no navigation, no reload), the effect never
    // re-runs and the date rollover is missed until a manual refresh.
    // Re-run on visibility/focus so foregrounding the app also catches it.
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        runCheck();
      }
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("focus", runCheck);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("focus", runCheck);
    };
  }, [location.pathname]);

  const handleOk = () => {
    setShowTillModal(false);
    const posConfig = usePOSConfigStore.getState().posConfig;
    if (posConfig?.showDenominations === "N") {
      history.push("/close-till-all");
    } else {
      history.push("/close-till");
    }
  };

  return (
    <Modal open={showTillModal} footer={null} closable={false} maskClosable={false} width={450} centered styles={{ body: { padding: "32px", borderRadius: "35px" } }}>
      <div style={{ display: "flex", justifyContent: "center", marginBottom: "24px" }}>
        <div
          style={{
            width: "48px",
            height: "48px",
            backgroundColor: "#FEE4E2",
            borderRadius: "999px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <WarningOutlined style={{ color: "#D92D20", fontSize: "20px" }} />
        </div>
      </div>
      <h2 style={{ fontSize: "18px", fontWeight: 600, color: "#101828", marginBottom: "12px" }}>Till Closure Required</h2>
      <p style={{ fontSize: "14px", color: "#475467", marginBottom: "24px" }}>You have a pending Till session from a previous day. Please close the Till before continuing.</p>
      <div
        style={{
          backgroundColor: "#F9FAFB",
          borderRadius: "8px",
          padding: "12px 16px",
          fontSize: "11px",
          color: "#475467",
          marginBottom: "24px",
          textAlign: "center",
        }}
      >
        Click <strong>"Okay"</strong> to proceed to the Close Till page.
      </div>
      <div style={{ textAlign: "right" }}>
        <button
          onClick={handleOk}
          style={{
            backgroundColor: "#2970FF",
            border: "none",
            color: "#FFFFFF",
            padding: "8px 16px",
            fontSize: "14px",
            borderRadius: "8px",
            cursor: "pointer",
          }}
        >
          Okay
        </button>
      </div>
    </Modal>
  );
};

export default TillModalHandler;
